/* eslint-disable max-lines */
import * as t from 'io-ts';

import {
  CoreTrackingConsentDetails,
  TrackingConsentOptionalData,
  ViewState,
} from '@transcend-io/airgap.js-types';
import {
  PreferenceStoreIdentifier,
  PreferenceStoreWorkflowSettings,
} from '@transcend-io/privacy-types';
import { valuesOf } from '@transcend-io/type-utils';

// ////////////////////////////
// ///// Security Codecs //////
// ////////////////////////////
// Any security related codecs go here

/**
 * The shape of the JWT payload sent by gql backend to preference management service.
 * This is a flexible record that can contain any key-value pairs.
 */
export const BackendPrefMgmtJWTPayloadCodec = t.record(t.string, t.unknown);

/** Override types. */
export type BackendPrefMgmtJWTPayloadCodec = t.TypeOf<
  typeof BackendPrefMgmtJWTPayloadCodec
>;

// //////////////////////////////
// ///// Preferences Codecs /////
// //////////////////////////////
// Any preference related codecs go here

/**
 * The boolean preference choice type.
 */
const BooleanPreference = t.type({
  booleanValue: t.boolean,
});

/**
 * The select preference choice type.
 */
const SelectPreference = t.type({
  selectValue: t.string,
});

/**
 * The multi-select preference choice type.
 */
const MultiSelectPreference = t.type({
  selectValues: t.array(t.string),
});

/**
 * The format of preference values.
 * Supports boolean, string, array of strings, or undefined.
 *
 * Example values: `true, "daily", ["email", "sms"]`
 */
export const PreferenceValues = t.union([
  t.boolean,
  t.string,
  t.array(t.string),
]);

/** Override types. */
export type PreferenceValues = t.TypeOf<typeof PreferenceValues>;

/**
 * Preferences are key-value pairs where the key is a preference slug and the value is user preference.
 * Example: `{ "EmailMarketing": true, "SmsNotifications": ["promotions", "alerts"] }`
 */
export const Preferences = t.record(t.string, PreferenceValues);

/** Override types. */
export type Preferences = t.TypeOf<typeof Preferences>;

/**
 *  Preferences with null values that will be used to delete preferences.
 *  Null values indicate that the preference should be removed.
 *
 *  Example: `{ "EmailMarketing": true, "SmsNotifications": null }`
 */
export const PreferencesWithNulls = t.record(
  t.string,
  t.union([PreferenceValues, t.null]),
);

/** Override types. */
export type PreferencesWithNulls = t.TypeOf<typeof PreferencesWithNulls>;

/**
 * Purpose to preferences mapping.
 * Key is the purpose slug, value is the preferences object.
 *
 * Example: `{ "Marketing": { "EmailMarketing": true, "SmsNotifications": ["alerts"] } }`
 */
export const PurposeToPreferences = t.record(t.string, Preferences);

/** Override types. */
export type PurposeToPreferences = t.TypeOf<typeof PurposeToPreferences>;

/** Purpose to preferences mapping with null values. */
export const PurposeToPreferencesWithNulls = t.record(
  t.string,
  t.union([PreferencesWithNulls, t.null]),
);

/** Override types. */
export type PurposeToPreferencesWithNulls = t.TypeOf<
  typeof PurposeToPreferencesWithNulls
>;

/**
 * The shape of a preference object.
 *
 * Includes the topic and the choice which can be boolean, select, or multi-select.
 *
 * Example: `{ topic: "EmailMarketing", choice: { booleanValue: true } }`
 */
export const Preference = t.type({
  /** preference topic */
  topic: t.string,
  /** preference choice */
  choice: t.union([BooleanPreference, SelectPreference, MultiSelectPreference]),
});

/** Override types. */
export type Preference = t.TypeOf<typeof Preference>;

/**
 * The shape of a preference object that allows null choice.
 *
 * Includes the topic and the choice which can be boolean, select, multi-select, or null.
 *
 * _Null choice indicates that the preference should be removed._
 *
 * Example: `{ topic: "EmailMarketing", choice: null }`
 */
export const PreferenceWithNulls = t.type({
  /** preference topic */
  topic: t.string,
  /** preference choice */
  choice: t.union([
    BooleanPreference,
    SelectPreference,
    MultiSelectPreference,
    t.null,
  ]),
});

/** Override types. */
export type PreferenceWithNulls = t.TypeOf<typeof PreferenceWithNulls>;

// ////////////////////////////
// ///// Purposes Codecs //////
// ////////////////////////////
// Any purpose related codecs go here

/**
 * A record of key-value pairs where the key is a purpose slug and the value is user consent.
 *
 * Example: `{ "Marketing": true, "Analytics": false, "SaleOfInfo": undefined }`
 *
 * - Used in DynamoDB model for `ConsentPreferences` table.
 */
export const PreferenceStorePurposes = t.record(
  t.string,
  t.union([t.boolean, t.undefined]),
);

/** Override types. */
export type PreferenceStorePurposes = t.TypeOf<typeof PreferenceStorePurposes>;

/**
 *  The shape of a purpose record.
 *
 *  Includes:
 *  - purpose: The purpose slug (e.g., "Marketing").
 *  - consent: User consent for the purpose (true/false).
 *  - timestamp: ISO string of when the consent was set.
 *  - preferences: (Optional) Associated preferences for the purpose which is a key-value record.
 *  - workflowSettings: (Optional) The workflow settings for the purpose.
 *  - asyncProcessingEnabled: (Optional) If async processing is enabled for the purpose.
 *
 *  Example:
 *  ```
 *  {
 *  purpose: "Marketing",
 *  consent: true,
 *  timestamp: "2023-10-03T15:26:14.123Z",
 *  preferences: { "EmailMarketing": true, "SmsNotifications": ["alerts"] },
 *  workflowSettings: { ... },
 *  asyncProcessingEnabled: false
 *  }
 *  ```
 *
 * @deprecated Use `PurposePreferenceEntry` instead
 * TODO: https://linear.app/transcend/issue/PIK-315/ - Clean up types after backend uses new contract
 */
export const PreferenceStorePurpose = t.intersection([
  t.type({
    /** purpose */
    purpose: t.string,
    /** user consent */
    consent: t.boolean,
    /** timestamp */
    timestamp: t.string,
  }),
  t.partial({
    /** Associated preferences */
    preferences: Preferences,
    /** The workflow setting for the purpose */
    workflowSettings: PreferenceStoreWorkflowSettings,
    /** If async processing is enabled for the purpose */
    asyncProcessingEnabled: t.boolean,
  }),
]);

/** Override types. */
export type PreferenceStorePurpose = t.TypeOf<typeof PreferenceStorePurpose>;

/**
 * Information related to a single purpose.
 *
 * Takes in null values at purpose and preference level
 *  - Intended to used as an input to the lambda
 *
 *  @deprecated Use `PurposePreferenceEntryWithNulls` instead
 *  TODO: https://linear.app/transcend/issue/PIK-315/ - Clean up types after backend uses new contract
 */
export const PreferenceStorePurposeWithNulls = t.intersection([
  t.type({
    /** purpose */
    purpose: t.string,
    /** user consent */
    consent: t.union([t.boolean, t.null]),
    /** timestamp */
    timestamp: t.string,
  }),
  t.partial({
    /** Associated preferences */
    preferences: t.union([t.null, PreferencesWithNulls]),
    /** The workflow setting for the purpose */
    workflowSettings: PreferenceStoreWorkflowSettings,
    /** If async processing is enabled for the purpose */
    asyncProcessingEnabled: t.boolean,
  }),
]);

/** Override types. */
export type PreferenceStorePurposeWithNulls = t.TypeOf<
  typeof PreferenceStorePurposeWithNulls
>;

/**
 *  The shape of a purpose entry.
 *
 *  Includes:
 *  - purpose: The purpose slug (e.g., "Marketing").
 *  - consent: User consent for the purpose (true/false).
 *  - timestamp: ISO string of when the consent was set.
 *  - preferences: (Optional) Associated preferences for the purpose which is an array of Preference objects.
 *  - workflowSettings: (Optional) The workflow settings for the purpose.
 *  - asyncProcessingEnabled: (Optional) If async processing is enabled for the purpose.
 *
 *  Example:
 *  ```
 *  {
 *  purpose: "Marketing",
 *  consent: true,
 *  timestamp: "2023-10-03T15:26:14.123Z",
 *  preferences: [ { topic: "EmailMarketing", choice: { booleanValue: true } } ],
 *  workflowSettings: { ... },
 *  asyncProcessingEnabled: false
 *  }
 *  ```
 *
 */
export const PurposePreferenceEntry = t.intersection([
  t.type({
    /** purpose */
    purpose: t.string,
    /** user consent */
    consent: t.boolean,
    /** timestamp */
    timestamp: t.string,
  }),
  t.partial({
    /** Associated preferences */
    preferences: t.array(Preference),
    /** The workflow setting for the purpose */
    workflowSettings: PreferenceStoreWorkflowSettings,
    /** If async processing is enabled for the purpose */
    asyncProcessingEnabled: t.boolean,
  }),
]);

/** Override types. */
export type PurposePreferenceEntry = t.TypeOf<typeof PurposePreferenceEntry>;

/**
 * The shape of a purpose entry with null values.
 *
 * _Null values indicate that the purpose or preference should be removed._
 *
 * Includes:
 * - purpose: The purpose slug (e.g., "Marketing").
 * - consent: User consent for the purpose (true/false).
 * - timestamp: ISO string of when the consent was set.
 * - preferences: (Optional) Associated preferences for the purpose which is an array of Preference objects.
 * - workflowSettings: (Optional) The workflow settings for the purpose.
 * - asyncProcessingEnabled: (Optional) If async processing is enabled for the purpose.
 *
 * Example:
 * ```
 * {
 *  purpose: "Marketing",
 *  consent: true,
 *  timestamp: "2023-10-03T15:26:14.123Z",
 *  preferences: [ { topic: "EmailMarketing", choice: { booleanValue: true } } ],
 *  workflowSettings: { ... },
 *  asyncProcessingEnabled: false
 * }
 * ```
 */
export const PurposePreferenceEntryWithNulls = t.intersection([
  t.type({
    /** purpose */
    purpose: t.string,
    /** user consent */
    consent: t.union([t.boolean, t.null]),
    /** timestamp */
    timestamp: t.string,
  }),
  t.partial({
    /** Associated preferences */
    preferences: t.union([t.null, t.array(PreferenceWithNulls)]),
    /** The workflow setting for the purpose */
    workflowSettings: PreferenceStoreWorkflowSettings,
    /** If async processing is enabled for the purpose */
    asyncProcessingEnabled: t.boolean,
  }),
]);

/** Override types. */
export type PurposePreferenceEntryWithNulls = t.TypeOf<
  typeof PurposePreferenceEntryWithNulls
>;

/**
 * Preference store consent record with per purpose information
 *
 * Takes in null values at purpose and preference level
 * Null values indicate that the purpose or preference should be removed.
 *
 * @deprecated Use `PreferenceStoreConsentDetailsWithNullsV2` instead
 * TODO: https://linear.app/transcend/issue/PIK-315 - Clean up types after backend uses new contract
 */
export const PreferenceStoreConsentDetailsWithNulls = t.intersection([
  CoreTrackingConsentDetails,
  TrackingConsentOptionalData,
  t.type({
    /** Tracking consent config */
    purposes: t.array(PreferenceStorePurposeWithNulls),
  }),
]);

/** Override types. */
export type PreferenceStoreConsentDetailsWithNulls = t.TypeOf<
  typeof PreferenceStoreConsentDetailsWithNulls
>;

/**
 * Preference store consent record with per purpose information
 *
 * Takes in null values at purpose and preference level
 * _Null values indicate that the purpose or preference should be removed._
 */
export const PreferenceStoreConsentDetailsWithNullsV2 = t.intersection([
  CoreTrackingConsentDetails,
  TrackingConsentOptionalData,
  t.type({
    /** Tracking consent config */
    purposes: t.array(PurposePreferenceEntryWithNulls),
  }),
]);

/** Override types. */
export type PreferenceStoreConsentDetailsWithNullsV2 = t.TypeOf<
  typeof PreferenceStoreConsentDetailsWithNullsV2
>;

// ///////////////////////////////////
// /// PS Record components Codecs ///
// ///////////////////////////////////
// Any components of Preference Store Record Codecs go here
// Components: Are reusable parts of a full Preference Store Record

/** Consent Metadata */
export const ConsentMetadata = t.intersection([
  t.partial({
    tcmp: t.partial({
      tcf: t.partial({
        tcString: t.string,
      }),
    }),
  }),
  t.UnknownRecord,
]);

/**
 * Override type
 */
export type ConsentMetadata = t.TypeOf<typeof ConsentMetadata>;

/**
 * Required attributes for a preference store record in dynamo.
 *
 * Regardless of table used (ConsentPreferences or ConsentPreferencesByCategory).
 * These fields are required to create a new record in dynamo.
 */
export const PreferenceStoreRecordRequiredAttributes = t.type({
  /**
   * A misnomered field which is actually the primary key of the record in dynamo.
   * The value is a combination of an identifier + partition as `${identifier} ${partition}`
   *
   * Previously the identifier used was always an encrypted identifier,
   * but with the introduction of TranscendId this is no longer the case.
   *
   * Example: `encrypted-email== p123` or `transcend-id-uuid p123`
   */
  userId: t.string,
  /**
   * Again a misnomer because this value is not necessarily encrypted.
   * Best way to  think of this as splitting the primary key to get the identifier portion.
   *
   * Example: `encrypted-email==` or `transcend-id-uuid`
   */
  encryptedIdentifier: t.string,
  /**
   * The partition id used to segment users.
   *
   * This could either be the airgapBundleId or airgapPartitionId depending on the partition strategy used.
   *
   * Example: `partition-uuid` or `airgapBundle-uuid`
   */
  partition: t.string,
  /**
   * The Timestamp(ISO string formatted) when the change occurred on client
   *
   * This is sent by the clients (airgap.js/sombra) and should be in ISO 8601 format.
   *
   * Example: `2023-10-03T15:26:14.123Z`
   */
  timestamp: t.string,
});

/** Override types. */
export type PreferenceStoreRecordRequiredAttributes = t.TypeOf<
  typeof PreferenceStoreRecordRequiredAttributes
>;

/**
 * The Interactive Advertising Bureau (IAB) standard attributes for consent records.
 *
 * These fields are optional and may not be present in all records.
 */
export const PreferenceStoreRecordIABAttributes = t.partial({
  /**
   * TCF: EU GDPR consent string
   *
   * Example: `CPokAsAPokAsABEACBENC7CgAP_AAH_AAAwIAAAAAAAA`
   */
  tcf: t.string,
  /**
   * USP: US privacy string (CCPA/CPRA)
   *
   * This is a deprecated field in favor of extracting USP from GPP.
   *
   * Example: `1YNN`
   */
  usp: t.string,
  /**
   * GPP: Global Privacy Platform string
   *
   * Example: `Header~section1~section2~section3~section4~section5..~1YYN`
   */
  gpp: t.string,
});

/** Override types. */
export type PreferenceStoreRecordIABAttributes = t.TypeOf<
  typeof PreferenceStoreRecordIABAttributes
>;

/**
 * Optional airgap.js attributes for consent records.
 */
export const PreferenceStoreRecordAirgapAttributes = t.partial({
  /**
   * The view state of Transcend UI
   *
   * Example: `CompleteOptions`, `CompleteOptionsInverted`
   */
  viewState: valuesOf(ViewState),
  /**
   * The semver version of airgap.js used to capture the consent
   *
   * Example: `1.2.3`
   */
  airgapVersion: t.string,
});

/** Override types. */
export type PreferenceStoreRecordAirgapAttributes = t.TypeOf<
  typeof PreferenceStoreRecordAirgapAttributes
>;

/**
 * The base shape of a preference store record regardless of how purposes/metadata are stored.
 *
 * Airgap.js
 * - Expects purposes to be record of purpose to boolean mapping
 * - Expects metadata to be a parsed object.
 * - Expects purposeToPreferences to be a record of purpose to preferences mapping
 *
 * While Admin APIs
 * - Expects purposes to be an array of purpose records including consent and preferences
 * - Expects metadata to be a string
 *
 * Hence this base shape does not include purposes, purposeToPreferences and metadata fields.
 */
export const PreferenceStoreNoPurposesNoMetadataCodec = t.intersection([
  PreferenceStoreRecordRequiredAttributes,
  PreferenceStoreRecordAirgapAttributes,
  PreferenceStoreRecordIABAttributes,
  t.type({
    /** is consent confirmed */
    confirmed: t.boolean,
    /** The identifiers associated to a user */
    identifiers: t.array(PreferenceStoreIdentifier),
  }),
  t.partial({
    /** The metadata timestamp (ISO string formatted) of when it was last updated */
    metadataTimestamp: t.string,
    /** The ISO string formatted timestamp of when the record was last updated */
    updatedAt: t.string,
  }),
]);

/** Override types. */
export type PreferenceStoreNoPurposesNoMetadataCodec = t.TypeOf<
  typeof PreferenceStoreNoPurposesNoMetadataCodec
>;

// //////////////////////////////
// ///// Identifier Codecs //////
// //////////////////////////////
// Any identifier related codecs go here
/**
 * The shape of Preference store identifier.
 * Includes a `unique` field to indicate if the identifier is unique.
 *
 * Example: `{ name: "email", value: "encrypted-email==", unique: true }`
 */
export const EnrichedPreferenceStoreIdentifier = t.intersection([
  PreferenceStoreIdentifier,
  t.type({
    unique: t.boolean,
  }),
]);

/** Override types. */
export type EnrichedPreferenceStoreIdentifier = t.TypeOf<
  typeof EnrichedPreferenceStoreIdentifier
>;

// //////////////////////////////
// ///// Pagination Codecs //////
// //////////////////////////////
// Any pagination related codecs go here
/**
 * The shape of the last key used for pagination in DynamoDB queries.
 *
 * Includes optional fields for timestamp, updatedAt, and linkedIdentifier.
 *
 * Example:
 * ```
 * {
 *   userId: "encrypted-email== p123",
 *   partition: "p123",
 *   timestamp: "2023-10-03T15:26:14.123Z", // optional
 *   updatedAt: "2023-10-03T15:30:00.000Z", // optional
 *   linkedIdentifier: "some-linked-id" // optional
 * }
 * ```
 */
export const ConsentLastKey = t.intersection([
  t.type({
    /** encrypted identifier + partition delimited by space */
    userId: t.string,
    /** partition */
    partition: t.string,
  }),
  t.partial({
    /** timestamp */
    timestamp: t.string,
    /** updated at */
    updatedAt: t.string,
    /** The linked identifier of the record where the previous query stopped */
    linkedIdentifier: t.string,
  }),
]);

/** Override types. */
export type ConsentLastKey = t.TypeOf<typeof ConsentLastKey>;

/* eslint-enable max-lines */
